import { useEffect, useRef, useState } from "react";

import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import { hydrateFilesFromPersisted } from "../../composerDraftAttachments";
import { composerFileAttachmentStore } from "../../composerFileAttachmentStore";
import type { ExpandedImagePreview } from "../chat/ExpandedImagePreview";
import { useComposerImageAttachments } from "../chat/useComposerImageAttachments";

interface UseNewThreadComposerImagesInput {
  readonly isDisabled: () => boolean;
  readonly onAfterDrop: () => void;
  readonly images?: readonly ComposerImageAttachment[];
  readonly files?: readonly ComposerFileAttachment[];
  readonly onImagesChange?: (images: ComposerImageAttachment[]) => void;
  readonly onFilesChange?: (files: ComposerFileAttachment[]) => void;
}

interface StagedImageOwnership {
  images: ComposerImageAttachment[];
  readonly transferReservationIds: Set<string>;
}

export function useNewThreadComposerImages(input: UseNewThreadComposerImagesInput) {
  const { onFilesChange } = input;
  const [images, setImages] = useState<ComposerImageAttachment[]>([]);
  const [files, setFiles] = useState<ComposerFileAttachment[]>([]);
  const currentImages = input.images ?? images;
  const currentFiles = input.files ?? files;
  const currentFilesRef = useRef(currentFiles);
  currentFilesRef.current = currentFiles;
  const ownership = useRef<StagedImageOwnership>({
    images: [...currentImages],
    transferReservationIds: new Set<string>(),
  }).current;
  ownership.images = [...currentImages];
  const [error, setError] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);

  useEffect(() => {
    return () => {
      for (const image of ownership.images) {
        if (
          !ownership.transferReservationIds.has(image.id) &&
          image.previewUrl.startsWith("blob:")
        ) {
          URL.revokeObjectURL(image.previewUrl);
        }
      }
    };
  }, [ownership]);

  const addImages = (addedImages: ComposerImageAttachment[]) => {
    const nextImages = [...ownership.images, ...addedImages];
    ownership.images = nextImages;
    if (input.onImagesChange) input.onImagesChange(nextImages);
    else setImages(nextImages);
  };

  const removeImage = (imageId: string) => {
    const removed = ownership.images.find((image) => image.id === imageId);
    if (removed?.previewUrl.startsWith("blob:")) URL.revokeObjectURL(removed.previewUrl);
    const nextImages = ownership.images.filter((image) => image.id !== imageId);
    ownership.images = nextImages;
    if (input.onImagesChange) input.onImagesChange(nextImages);
    else setImages(nextImages);
  };

  const addFiles = (addedFiles: ComposerFileAttachment[]) => {
    const knownIds = new Set(currentFilesRef.current.map((file) => file.id));
    const nextFiles = [
      ...currentFilesRef.current,
      ...addedFiles.filter((file) => !knownIds.has(file.id)),
    ];
    currentFilesRef.current = nextFiles;
    if (input.onFilesChange) input.onFilesChange(nextFiles);
    else setFiles(nextFiles);
  };

  const removeFile = (fileId: string) => {
    const nextFiles = currentFiles.filter((file) => file.id !== fileId);
    if (input.onFilesChange) input.onFilesChange(nextFiles);
    else setFiles(nextFiles);
  };

  useEffect(() => {
    const pending = currentFiles.filter((attachment) => attachment.file === null);
    if (pending.length === 0) return;
    let cancelled = false;
    const expectedIds = pending.map((attachment) => attachment.id);
    void hydrateFilesFromPersisted(pending, composerFileAttachmentStore)
      .then((hydrated) => {
        if (cancelled || hydrated === null) return;
        const currentPendingIds = currentFilesRef.current
          .filter((attachment) => attachment.file === null)
          .map((attachment) => attachment.id);
        if (
          currentPendingIds.length !== expectedIds.length ||
          currentPendingIds.some((id, index) => id !== expectedIds[index])
        )
          return;
        const restoredById = new Map(hydrated.map((attachment) => [attachment.id, attachment]));
        const nextFiles = currentFilesRef.current.map(
          (attachment) => restoredById.get(attachment.id) ?? attachment,
        );
        if (onFilesChange) onFilesChange(nextFiles);
        else setFiles(nextFiles);
      })
      .catch(() => {
        if (!cancelled) setError("Could not restore draft attachments.");
      });
    return () => {
      cancelled = true;
    };
  }, [currentFiles, onFilesChange]);

  const replaceImages = (nextImages: ComposerImageAttachment[]) => {
    for (const image of ownership.images) {
      if (
        !nextImages.some((next) => next.id === image.id) &&
        image.previewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(image.previewUrl);
      }
    }
    ownership.images = nextImages;
    if (input.onImagesChange) input.onImagesChange(nextImages);
    else setImages(nextImages);
  };

  const releaseTransferredImages = (transferredImages: ReadonlyArray<ComposerImageAttachment>) => {
    const transferredIds = new Set(transferredImages.map((image) => image.id));
    const nextImages = ownership.images.filter((image) => !transferredIds.has(image.id));
    ownership.images = nextImages;
    for (const imageId of transferredIds) ownership.transferReservationIds.delete(imageId);
    if (input.onImagesChange) input.onImagesChange(nextImages);
    else setImages(nextImages);
  };

  const reserveImagesForTransfer = (transferredImages: ReadonlyArray<ComposerImageAttachment>) => {
    for (const image of transferredImages) ownership.transferReservationIds.add(image.id);
  };

  const cancelImageTransfer = (transferredImages: ReadonlyArray<ComposerImageAttachment>) => {
    for (const image of transferredImages) ownership.transferReservationIds.delete(image.id);
  };

  const controller = useComposerImageAttachments({
    images: currentImages,
    files: currentFiles,
    scopeKey: "new-thread",
    isDisabled: input.isDisabled,
    onAddImages: addImages,
    onAddFiles: addFiles,
    onError: setError,
    onAfterDrop: input.onAfterDrop,
  });

  return {
    images: currentImages,
    files: currentFiles,
    error,
    expandedImage,
    controller,
    removeImage,
    removeFile,
    replaceImages,
    onExpandImage: setExpandedImage,
    closeExpandedImage: () => setExpandedImage(null),
    reserveImagesForTransfer,
    cancelImageTransfer,
    releaseTransferredImages,
  };
}
